/**
 * The daily digest: what one person's email says, and how it is written.
 *
 * ## Whose figures these are
 *
 * The content arrives from `digest_content()`, which computes it **as the
 * recipient** — the same row-level security, the same `worklist_summary()`
 * their Today screen calls. Nothing here filters or widens anything; a
 * Collector's digest contains their accounts because the database gave it
 * nothing else. This file reads that answer, decides whether it is empty, and
 * writes it down.
 *
 * ## Empty is a fact, never a fallback
 *
 * `readDigestContent()` throws on anything it cannot read. A digest that
 * failed to load must fail the delivery, not arrive as a cheerful "nothing to
 * do today" — the second is the kind of email that gets trusted.
 *
 * ## What the words may claim
 *
 * Every figure is one the product defines already (18-REPORT-METRICS): what is
 * owed now, follow-ups by state, promises, cheques by state. A promise is
 * called a promise and never money received; an outstanding balance is the
 * current position and never a historical receipt. Nothing is inferred.
 *
 * ## Escaping
 *
 * Company names come from a form, customer names from a spreadsheet and
 * people's names from whatever they typed. Every one of them is escaped before
 * it reaches the HTML part, and control characters are stripped before any of
 * them reaches the subject line — a line break in a company name must not
 * become a header. There is no template language here at all: this is fixed
 * product copy with values inserted, so there is nothing to interpret twice.
 */
import { readWorklistSummary, dayIsClear, type WorklistFigure, type WorklistTotals } from './worklist.js';
import { createMoneyFormatter, type MoneyFormatter } from './format.js';
import { escapeHtml } from './templateVariables.js';
import { CHEQUE_STATES, type ChequeState } from './cheques.js';

export interface DigestWorkspace {
    name: string;
    slug: string;
    currency: string;
    locale: string;
    timezone: string;
}

export interface DigestAccount {
    name: string;
    amount: number;
    type: 'Dr' | 'Cr';
    /** The follow-up date that has passed, as the company's calendar day. */
    followUpDate: string | null;
}

export interface DigestTeamLine {
    name: string;
    overdue: number;
    today: number;
}

/** The four cheque states that are work, in the order Today shows them. */
export const DIGEST_CHEQUE_STATES = ['overdue', 'due', 'bounced', 'hold'] as const satisfies readonly ChequeState[];
export type DigestChequeState = (typeof DIGEST_CHEQUE_STATES)[number];

export interface DigestContent {
    /** `company` for someone who may see the whole book, `own` otherwise. */
    scope: 'company' | 'own';
    /** The company's calendar day the figures were computed on. */
    today: string;
    totals: WorklistTotals;
    /** Null when the recipient may not see cheques at all. */
    cheques: Record<DigestChequeState, WorklistFigure> | null;
    overdueAccounts: DigestAccount[];
    /** Null unless the recipient may see the whole book. */
    team: DigestTeamLine[] | null;
}

export class DigestContentError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'DigestContentError';
    }
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

const asRecord = (value: unknown, what: string): Record<string, unknown> => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new DigestContentError(`digest ${what} is missing`);
    }
    return value as Record<string, unknown>;
};

const count = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const amount = (value: unknown): number => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

/**
 * The database's answer, checked.
 *
 * Strict about shape — a missing summary is an error, not a zero — and
 * forgiving about nothing else. Names are kept as the text they are; escaping
 * happens where they are written, not here.
 */
export function readDigestContent(raw: unknown): DigestContent {
    const root = asRecord(raw, 'content');
    const scope = root['scope'];
    if (scope !== 'company' && scope !== 'own') throw new DigestContentError('digest scope is not recognised');
    const today = String(root['today'] ?? '');
    if (!ISO_DAY.test(today)) throw new DigestContentError('digest date is not a date');

    const summary = asRecord(root['summary'], 'summary');
    if (!('overdue_count' in summary) || !('today_count' in summary)) {
        throw new DigestContentError('digest summary is incomplete');
    }

    let cheques: DigestContent['cheques'] = null;
    if (root['cheques'] !== null && root['cheques'] !== undefined) {
        const c = asRecord(root['cheques'], 'cheque figures');
        cheques = {
            overdue: { count: count(c['overdue_count']), amount: amount(c['overdue_amount']) },
            due: { count: count(c['due_count']), amount: amount(c['due_amount']) },
            bounced: { count: count(c['bounced_count']), amount: amount(c['bounced_amount']) },
            hold: { count: count(c['hold_count']), amount: amount(c['hold_amount']) },
        };
    }

    const accountsRaw = root['overdue_accounts'];
    if (!Array.isArray(accountsRaw)) throw new DigestContentError('digest account list is missing');
    const overdueAccounts: DigestAccount[] = accountsRaw.slice(0, 10).map(item => {
        const a = asRecord(item, 'account');
        const follow = a['follow_up_date'];
        return {
            name: String(a['name'] ?? ''),
            amount: amount(a['amount']),
            type: a['type'] === 'Cr' ? 'Cr' : 'Dr',
            followUpDate: typeof follow === 'string' && ISO_DAY.test(follow) ? follow : null,
        };
    });

    let team: DigestTeamLine[] | null = null;
    if (Array.isArray(root['team'])) {
        team = (root['team'] as unknown[]).slice(0, 10).map(item => {
            const t = asRecord(item, 'team line');
            return { name: String(t['name'] ?? ''), overdue: count(t['overdue']), today: count(t['today']) };
        });
    }

    return { scope, today, totals: readWorklistSummary(summary), cheques, overdueAccounts, team };
}

/** Whether any cheque needs doing something to. */
export const chequesNeedAttention = (content: DigestContent): boolean =>
    content.cheques !== null && DIGEST_CHEQUE_STATES.some(s => content.cheques![s].count > 0);

/**
 * Genuinely nothing to report: nothing overdue, due today, urgent or owed
 * without a plan (Today's own definition of a clear day), no promise falling
 * due today, and no cheque needing action.
 */
export const digestIsEmpty = (content: DigestContent): boolean =>
    dayIsClear(content.totals)
    && content.totals.promisedToday.count === 0
    && !chequesNeedAttention(content);

/* ------------------------------ writing it ------------------------------- */

export interface DigestEmailInput {
    workspace: DigestWorkspace;
    recipientName: string;
    content: DigestContent;
    /** From configuration, never from a request. */
    appOrigin: string;
    /** When the figures were read, shown in the company's zone. */
    generatedAt: Date;
    /** A test send says so, in the subject and at the top. */
    test?: boolean;
}

export interface DigestEmail {
    subject: string;
    text: string;
    html: string;
}

/** Control characters out, whitespace collapsed: safe for a header line. */
export const headerSafe = (value: string, max = 120): string =>
    // eslint-disable-next-line no-control-regex
    value.replace(/[\x00-\x1F\x7F]+/g, ' ').replace(/\s+/g, ' ').trim().slice(0, max);

function moneyFor(workspace: DigestWorkspace): MoneyFormatter {
    try {
        return createMoneyFormatter({ locale: workspace.locale, currency: workspace.currency });
    } catch {
        // A locale or currency Intl does not know must not stop the email; the
        // figure is still right, only its symbol is spelled out.
        const plain = new Intl.NumberFormat('en', { maximumFractionDigits: 2 });
        const code = headerSafe(workspace.currency, 3).toUpperCase();
        const show = (v: number | null | undefined) => `${code} ${plain.format(Math.abs(Number(v) || 0))}`;
        const minor = new Intl.NumberFormat('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return {
            exact: show, compact: show, balance: show, plain: v => plain.format(Math.abs(Number(v) || 0)),
            precise: v => `${code} ${minor.format(Math.abs(Number(v) || 0))}`,
        };
    }
}

function calendarDay(iso: string, locale: string, style: 'long' | 'full'): string {
    const d = new Date(`${iso}T12:00:00Z`);
    const options: Intl.DateTimeFormatOptions = style === 'full'
        ? { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }
        : { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
    try {
        return new Intl.DateTimeFormat(locale, options).format(d);
    } catch {
        return new Intl.DateTimeFormat('en', options).format(d);
    }
}

function clockTime(at: Date, locale: string, timeZone: string): string {
    const options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit', timeZone };
    try {
        return new Intl.DateTimeFormat(locale, options).format(at);
    } catch {
        return new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }).format(at) + ' UTC';
    }
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

interface Line { label: string; value: string }
interface Section { title: string; note?: string; lines: Line[] }

/**
 * The digest, in the words and the order both parts use.
 *
 * Built once as data and rendered twice, so the plain-text and HTML versions
 * cannot say different things.
 */
function sections(input: DigestEmailInput, money: MoneyFormatter): Section[] {
    const { content, workspace } = input;
    const t = content.totals;
    const figure = (f: WorklistFigure, noun: [string, string] = ['account', 'accounts']) =>
        f.amount < 0
            ? `${plural(f.count, ...noun)} · ${money.exact(f.amount)} net credit`
            : f.amount > 0 ? `${plural(f.count, ...noun)} · ${money.exact(f.amount)}` : plural(f.count, ...noun);

    const out: Section[] = [];

    const attention: Line[] = [];
    if (t.groups.overdue.count) attention.push({ label: 'Overdue follow-ups', value: figure(t.groups.overdue) });
    if (t.groups.today.count) attention.push({ label: 'Follow-ups due today', value: figure(t.groups.today) });
    if (t.groups.urgent.count) attention.push({ label: 'Marked urgent', value: plural(t.groups.urgent.count, 'account', 'accounts') });
    if (t.groups.no_follow_up.count) attention.push({ label: 'Owed, with no follow-up set', value: figure(t.groups.no_follow_up) });
    if (attention.length) out.push({ title: 'Needs attention', lines: attention });

    const promised: Line[] = [];
    if (t.promisedToday.count) promised.push({ label: 'Expected today', value: figure(t.promisedToday, ['promise', 'promises']) });
    if (t.promisedWeek.count) promised.push({ label: 'Expected in the next 7 days', value: figure(t.promisedWeek, ['promise', 'promises']) });
    if (promised.length) {
        out.push({
            title: 'Promised by customers',
            note: 'Promises and forecasts, not payments received.',
            lines: promised,
        });
    }

    if (content.cheques && chequesNeedAttention(content)) {
        out.push({
            title: 'Cheques needing attention',
            lines: DIGEST_CHEQUE_STATES
                .filter(s => content.cheques![s].count > 0)
                .map(s => ({
                    label: CHEQUE_STATES[s].label,
                    value: `${plural(content.cheques![s].count, 'cheque', 'cheques')} · ${money.exact(content.cheques![s].amount)}`,
                })),
        });
    }

    if (content.overdueAccounts.length) {
        out.push({
            title: 'Largest overdue accounts',
            lines: content.overdueAccounts.map(a => ({
                label: a.name || 'Unnamed account',
                value: `${money.exact(a.amount)}${a.type === 'Cr' ? ' (credit)' : ''}`
                    + (a.followUpDate ? ` · follow-up was due ${calendarDay(a.followUpDate, workspace.locale, 'long')}` : ''),
            })),
        });
    }

    if (content.team && content.team.length) {
        out.push({
            title: 'Your team',
            lines: content.team.map(m => ({
                label: m.name || 'Somebody',
                value: [m.overdue ? `${m.overdue} overdue` : '', m.today ? `${m.today} due today` : '']
                    .filter(Boolean).join(', ') || 'nothing waiting',
            })),
        });
    }
    return out;
}

/** The link the email opens: configuration plus the company's own slug. */
export const digestLink = (appOrigin: string, slug: string): string =>
    `${appOrigin.replace(/\/+$/, '')}/w/${encodeURIComponent(slug)}/today`;

export function renderDigestEmail(input: DigestEmailInput): DigestEmail {
    const { workspace, content } = input;
    const money = moneyFor(workspace);
    const company = headerSafe(workspace.name, 120) || 'your company';
    const empty = digestIsEmpty(content);
    const t = content.totals;
    const day = calendarDay(content.today, workspace.locale, 'full');
    const asOf = clockTime(input.generatedAt, workspace.locale, workspace.timezone);
    const scopeLine = content.scope === 'company' ? 'The whole company’s accounts' : 'The accounts assigned to you';
    const link = digestLink(input.appOrigin, workspace.slug);
    const greeting = headerSafe(input.recipientName, 80);

    const headline = empty
        ? `Nothing is overdue or due today in ${content.scope === 'company' ? 'the company’s accounts' : 'your accounts'}.`
        : [t.groups.overdue.count ? `${plural(t.groups.overdue.count, 'overdue follow-up', 'overdue follow-ups')}` : '',
           t.groups.today.count ? `${plural(t.groups.today.count, 'follow-up', 'follow-ups')} due today` : '']
            .filter(Boolean).join(' and ') || 'Some accounts need attention today.';

    const subjectFacts = empty
        ? 'nothing overdue or due today'
        : [t.groups.overdue.count ? `${t.groups.overdue.count} overdue` : '',
           t.groups.today.count ? `${t.groups.today.count} due today` : '']
            .filter(Boolean).join(', ') || 'accounts need attention';
    const subject = headerSafe(`${input.test ? '[Test] ' : ''}${company}: ${subjectFacts}`, 200);

    const owed = t.total.count
        ? `Currently owed: ${money.exact(t.total.amount)} across ${plural(t.total.count, 'account', 'accounts')}.`
        : '';
    const testNote = 'This is a test, sent because you asked for one from the reminder digest settings. It shows what your digest would contain right now.';
    const footer = `You receive this as a member of ${company} on Timely Payment, because its daily digest includes you. `
        + 'Whoever manages your company’s integrations can change who receives it. '
        + `Figures as of ${asOf} (${workspace.timezone}).`;

    const body = sections(input, money);

    const text = [
        'Timely Payment · Daily digest',
        company,
        `${day} · ${scopeLine}`,
        '',
        ...(input.test ? [testNote, ''] : []),
        ...(greeting ? [`Hello ${greeting},`, ''] : []),
        headline,
        ...(owed ? [owed] : []),
        '',
        ...body.flatMap(s => [
            s.title.toUpperCase(),
            ...(s.note ? [s.note] : []),
            ...s.lines.map(l => `  ${l.label}: ${l.value}`),
            '',
        ]),
        `Open today’s work: ${link}`,
        '',
        '—',
        footer,
    ].join('\n');

    const e = escapeHtml;
    const sectionHtml = body.map(s => `
      <tr><td style="padding:20px 0 6px;font-size:12px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#52525b">${e(s.title)}</td></tr>
      ${s.note ? `<tr><td style="padding:0 0 6px;font-size:12.5px;color:#71717a">${e(s.note)}</td></tr>` : ''}
      ${s.lines.map(l => `<tr><td style="padding:5px 0;font-size:14px;line-height:1.5;color:#18181b;border-bottom:1px solid #f0f0f1"><strong style="font-weight:600">${e(l.label)}</strong><br><span style="color:#3f3f46">${e(l.value)}</span></td></tr>`).join('')}`).join('');

    const html = `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width"><title>${e(subject)}</title></head>
<body style="margin:0;padding:24px 12px;background:#f4f4f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;color:#18181b">
  <table role="presentation" cellpadding="0" cellspacing="0" style="width:100%;max-width:560px;margin:0 auto;background:#ffffff;border-radius:12px;padding:28px">
    <tr><td style="font-size:12px;color:#71717a">Timely Payment · Daily digest</td></tr>
    <tr><td style="padding:4px 0 0;font-size:20px;font-weight:700">${e(company)}</td></tr>
    <tr><td style="padding:2px 0 16px;font-size:13px;color:#52525b">${e(day)} · ${e(scopeLine)}</td></tr>
    ${input.test ? `<tr><td style="padding:10px 12px;margin:0 0 12px;background:#fef3c7;border-radius:8px;font-size:13px;color:#78350f">${e(testNote)}</td></tr>` : ''}
    ${greeting ? `<tr><td style="padding:12px 0 0;font-size:15px">Hello ${e(greeting)},</td></tr>` : ''}
    <tr><td style="padding:8px 0 0;font-size:15px;line-height:1.55">${e(headline)}</td></tr>
    ${owed ? `<tr><td style="padding:4px 0 0;font-size:14px;color:#3f3f46">${e(owed)}</td></tr>` : ''}
    ${sectionHtml}
    <tr><td style="padding:24px 0 8px">
      <a href="${e(link)}" style="display:inline-block;background:#2563eb;color:#ffffff;text-decoration:none;padding:11px 20px;border-radius:8px;font-size:14px;font-weight:600">Open today’s work</a>
    </td></tr>
    <tr><td style="padding:16px 0 0;font-size:12px;line-height:1.55;color:#71717a">${e(footer)}</td></tr>
  </table>
</body></html>`;

    return { subject, text, html };
}

/* --------------------------- the run history ----------------------------- */

export type EmailRunStatus = 'pending' | 'sending' | 'sent' | 'partial' | 'failed' | 'skipped';

export type EmailRunCategory =
    | 'provider_unavailable' | 'provider_rejected' | 'provider_auth' | 'missing_credential'
    | 'invalid_configuration' | 'no_recipients' | 'empty_digest' | 'disabled'
    | 'workspace_inactive' | 'recipient_ineligible' | 'content_error' | 'expired'
    | 'interrupted' | 'unconfirmed' | 'schedule_changed' | 'internal_error';

/**
 * One sentence per reason, written for the person managing the schedule.
 *
 * Never the provider's own words: a provider's error body can carry the
 * recipient's address, an account id or a request id, none of which belongs
 * on a screen other people may be looking at.
 */
export const EMAIL_RUN_REASONS: Record<EmailRunCategory, string> = {
    provider_unavailable: 'The email service did not respond.',
    provider_rejected: 'The email service refused the message for at least one recipient.',
    provider_auth: 'The email service refused this deployment’s credentials.',
    missing_credential: 'Email sending is not set up on this deployment yet, so nothing was sent.',
    invalid_configuration: 'The sender address for this deployment is not set up correctly.',
    no_recipients: 'Nobody could receive it: no active member matches the chosen roles or addresses.',
    empty_digest: 'There was nothing to report, and “skip when empty” is on.',
    disabled: 'The digest was switched off before it went out.',
    workspace_inactive: 'The company is not active.',
    recipient_ineligible: 'The recipient is no longer an active member who can see accounts.',
    content_error: 'The digest could not be put together.',
    expired: 'It could not go out within twelve hours of its time, so it was not sent late.',
    interrupted: 'Sending stopped partway through.',
    unconfirmed: 'The email service never confirmed at least one message, so it may or may not have arrived.',
    schedule_changed: 'Another digest went out less than six hours earlier, after the time or time zone changed.',
    internal_error: 'Something went wrong on our side.',
};

export interface EmailRunSummary {
    kind: 'daily_digest' | 'test';
    status: EmailRunStatus;
    errorCategory: string | null;
    recipients: number;
    delivered: number;
    failed: number;
    attemptCount: number;
    maxAttempts: number;
    detail?: string | null;
}

export interface EmailRunDescription {
    label: string;
    tone: 'pos' | 'warn' | 'dang' | 'neutral';
    explanation: string | null;
}

const reasonFor = (category: string | null): string | null =>
    category && category in EMAIL_RUN_REASONS ? EMAIL_RUN_REASONS[category as EmailRunCategory] : null;

/** What the history row says. The status is the fact; the rest explains it. */
export function describeEmailRun(run: EmailRunSummary): EmailRunDescription {
    const reason = reasonFor(run.errorCategory);
    switch (run.status) {
        case 'sent':
            return {
                label: 'Sent', tone: 'pos',
                explanation: `Delivered to ${plural(run.delivered, 'recipient', 'recipients')}.`
                    + (run.detail ? ` ${run.detail}` : ''),
            };
        case 'partial':
            return {
                label: 'Partly sent', tone: 'warn',
                explanation: `Delivered to ${run.delivered} of ${run.recipients}. ${reason ?? ''}`.trim(),
            };
        case 'failed':
            return {
                label: 'Not sent', tone: 'dang',
                explanation: [
                    reason ?? 'It could not be sent.',
                    run.attemptCount > 1 ? `Gave up after ${run.attemptCount} attempts.` : '',
                ].filter(Boolean).join(' '),
            };
        case 'skipped':
            return { label: 'Skipped', tone: 'neutral', explanation: reason };
        case 'pending':
            return {
                label: run.attemptCount > 0 ? 'Retrying' : 'Queued', tone: run.attemptCount > 0 ? 'warn' : 'neutral',
                explanation: run.attemptCount > 0
                    ? `${reason ?? 'The last attempt did not finish.'} It will be tried again (attempt ${run.attemptCount + 1} of ${run.maxAttempts}).`
                    : 'Waiting for the scheduler to send it.',
            };
        case 'sending':
            return { label: 'Sending', tone: 'neutral', explanation: null };
    }
}
